// PlayerTeamPage.jsx
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { DashboardNavbar } from "../../../components/layout/DashboardNavbar";
import { PlayerTeamHeader } from "./PlayerTeamHeader";
import { PlayerTable } from "../../coach/coachTeams/PlayerTable";
import { CARD, CardTitle, InfoGrid } from "../../dashboard/profile/profileCard";
import { fetchPlayerTeamRoster } from '../../../features/playerProfile/playerProfileSlice';
import { formatLocation } from "../../../utils/formatLocation";

// Team page a logged-in player reaches from the team logo on their profile.
// Same look as the player's own profile: navy header, then white cards.
export const PlayerTeamPage = () => {
  const { teamId } = useParams();
  const dispatch = useDispatch();
  const { teamRoster, rosterPagination, rosterLoading, rosterError } = useSelector((state) => state.playerProfile);
  const team = teamRoster[0]?.team || {};

  // Fetch team roster using player API with teamId from route
  useEffect(() => {
    if (teamId) {
      dispatch(fetchPlayerTeamRoster({
        teamId,
        page: 1,
        limit: 20
      }));
    }
  }, [dispatch, teamId]);

  const handleLoadMore = () => {
    if (rosterPagination.hasMore && !rosterLoading) {
      dispatch(fetchPlayerTeamRoster({
        teamId,
        page: rosterPagination.currentPage + 1,
        limit: rosterPagination.limit
      }));
    }
  };

  return (
    <div className="min-h-screen bg-jp-page text-jp-ink text-left overflow-x-hidden">
      <DashboardNavbar userRole="player" />
      <PlayerTeamHeader teamData={team} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 space-y-6">
        {/* Loading State */}
        {rosterLoading && teamRoster.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <div className="w-12 h-12 border-4 border-jp-accent border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {/* Error State */}
        {rosterError && (
          <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/40 text-red-600 dark:text-red-400 text-sm">
            {rosterError}
          </div>
        )}

        {/* No Team Players */}
        {!rosterLoading && teamRoster.length === 0 && !rosterError && (
          <section className={`${CARD} text-center py-12`}>
            <p className="text-lg font-bold text-jp-ink">No players found</p>
            <p className="text-sm mt-2 text-jp-muted">This team doesn't have any players yet.</p>
          </section>
        )}

        {/* Team Content */}
        {teamRoster.length > 0 && (
          <>
            <section className={CARD}>
              <CardTitle title="Team Information" />
              <InfoGrid
                items={[
                  { label: 'Division', value: team.division },
                  { label: 'Conference', value: team.conference },
                  { label: 'Region', value: team.region },
                  { label: 'Location', value: formatLocation(team.location) },
                  { label: 'Head Coach', value: team.coachName },
                  { label: 'Rank', value: team.rank },
                  { label: 'Home', value: team.homeRecord || team.home },
                  { label: 'Away', value: team.awayRecord || team.away },
                  { label: 'Neutral', value: team.neutralRecord || team.neutral },
                ]}
              />
            </section>

            <section className={CARD}>
              <CardTitle title="Roster" />
              <PlayerTable
                data={teamRoster}
                loading={rosterLoading}
                pagination={rosterPagination}
                onLoadMore={handleLoadMore}
                isPlayerView={true}
              />
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default PlayerTeamPage;
